import { useNavigate } from "react-router-dom";
import useTranslation from "../../../client/hooks/useTranslation";
import { sessionObjectiveRatios, type SessionAccountPnl } from "../../../trading-sessions/SessionAccountsTable";
import { useLiteData } from "../../data/LiteDataProvider";
import { Card, FirmMark, Pnl, StatusBadge, toneColor } from "../../ui/ui";

const th = "px-3 py-2.5 font-medium whitespace-nowrap";

/**
 * Comptes tradés pendant la session, version Lite du tableau Advanced
 * (SessionAccountsTable) : mêmes colonnes et même barre d'objectif (part de la
 * session comprise, via sessionObjectiveRatios), au format des tableaux Lite.
 */
export function SessionAccounts({ pnlByAccount }: { pnlByAccount: SessionAccountPnl[] }) {
  const t = useTranslation();
  const tl = t.lite.sessions.detail;
  const { fmt, accountsById } = useLiteData();
  const navigate = useNavigate();
  const rows = pnlByAccount
    .filter(r => r.accountId && accountsById.has(r.accountId))
    .map(r => ({ account: accountsById.get(r.accountId!)!, sessionPnl: r.pnl }));
  if (!rows.length) return null;

  return (
    <Card title={t.sessionDetail.sessionAccountsTitle} pad={false}>
      <div className="overflow-x-auto mt-2">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-[11px] text-muted-foreground/70 border-y border-border">
              <th className={`${th} text-left pl-5`}>{t.dashboard.accountsTableName}</th>
              <th className={`${th} text-left`}>{t.dashboard.accountsTableStatus}</th>
              <th className={`${th} text-left`}>{t.dashboard.accountsTableStrategy}</th>
              <th className={`${th} text-right`}>{t.dashboard.accountsTableBalance}</th>
              <th className={`${th} text-right`}>{tl.sessionPnl}</th>
              <th className={`${th} text-left pr-5 w-[260px]`}>{t.dashboard.accountsTableObjective}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(({ account: a, sessionPnl }) => {
              const raw = a.raw as any;
              const challenge = raw.challengeProgress;
              const o = sessionObjectiveRatios(raw, sessionPnl);
              const pct = raw.initialBalance > 0 ? sessionPnl / raw.initialBalance : 0;
              return (
                <tr key={a.id} onClick={() => navigate(`/account/${a.id}`)} className="cursor-pointer hover:bg-muted/60 transition-colors">
                  <td className="pl-5 px-3 py-3">
                    <span className="flex items-center gap-2.5 min-w-0">
                      <FirmMark firm={a.firm} size="sm" />
                      <span className="min-w-0">
                        <span className="block font-medium truncate max-w-[220px]">{a.name}</span>
                        {a.firm && <span className="block text-xs text-muted-foreground truncate">{a.firm.name}{raw.activeTier?.label ? ` · ${raw.activeTier.label}` : ""}</span>}
                      </span>
                    </span>
                  </td>
                  <td className="px-3 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-3 py-3">
                    {raw.strategies?.length ? (
                      <span className="flex flex-wrap gap-1">
                        {raw.strategies.map((s: any) => (
                          <span key={s.id} className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground whitespace-nowrap">
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: s.color }} />{s.name}
                          </span>
                        ))}
                      </span>
                    ) : <span className="text-muted-foreground/70">-</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-mono tabular-nums whitespace-nowrap">{fmt.money(a.balance, { sign: false })}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <Pnl value={sessionPnl} decimals className="font-medium" />
                    <div className="text-xs text-muted-foreground font-mono">{pct >= 0 ? "+" : ""}{fmt.pct(pct, 2)}</div>
                  </td>
                  <td className="pr-5 px-3 py-3">
                    {o.hasObjectiveBar ? (
                      <div>
                        <div className="flex justify-between items-baseline text-xs mb-1.5 gap-2">
                          <span className="font-mono whitespace-nowrap">
                            {challenge.targetAmount !== null
                              ? `${fmt.money(challenge.profit, { sign: false })} / ${fmt.money(challenge.targetAmount, { sign: false })}`
                              : fmt.pct(o.currentRatio)}
                          </span>
                          <span className="font-mono text-muted-foreground whitespace-nowrap">{fmt.pct(o.currentRatio)}</span>
                        </div>
                        <ObjectiveBar past={o.pastRatio} current={o.currentRatio} session={o.sessionRatio} title={`${t.sessionDetail.sessionContribution} : ${fmt.money(sessionPnl, { decimals: true })}`} />
                      </div>
                    ) : <span className="text-muted-foreground/70">-</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Progression avant la session (accent atténué), puis la part de la session en gain ou en perte. */
function ObjectiveBar({ past, current, session, title }: { past: number; current: number; session: number; title: string }) {
  const up = session > 0;
  const segLeft = up ? past : current;
  const segWidth = up ? Math.min(1 - past, session) : Math.max(0, past - current);
  return (
    <div className="relative h-2 rounded-full bg-muted overflow-hidden" title={title}>
      <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${Math.min(1, past) * 100}%`, background: toneColor("brand", 0.45) }} />
      {session !== 0 && segWidth > 0 && (
        <div className="absolute inset-y-0" style={{ left: `${segLeft * 100}%`, width: `${segWidth * 100}%`, background: toneColor(up ? "gain" : "loss") }} />
      )}
    </div>
  );
}
